<template>
  <div>
    <div
      id="bowelCleanseForms"
      class="bowel-cleanse-forms print"
      v-if="examinationInfo"
    >
      <ul class="bowel-cleanse-forms__group bowel-cleanse-forms__group--first">
        <!-- indication -->
        <PreviewExamItem
          :title="$t('indication')"
          :value="removeOthersFromList(examinationInfo.indication, 7)"
          refKey="value"
          refValue="text"
          :refArr="getIndicationList"
        >
          <template #extra>
            <p v-if="examinationInfo.indicationOther">
              其他臨床目的施行之大腸鏡 :
              {{ examinationInfo.indicationOther }}
            </p>
          </template>
        </PreviewExamItem>
        <!-- preparationAgent -->
        <PreviewExamItem
          :title="$t('preparationAgent')"
          :value="examinationInfo.clean_medID"
          refKey="id"
          refValue="clean_med"
          :refArr="getCleanMedList"
        >
          <template #extra>
            <span v-if="examinationInfo.antispasmodicOther">
              其他 : {{ examinationInfo.antispasmodicOther }}
            </span>
          </template>
        </PreviewExamItem>
        <!--preparationTime  -->
        <PreviewExamItem
          :title="$t('preparationTime')"
          :value="[examinationInfo.preparationTime]"
          :needMapRef="false"
        />
        <!-- antiPlatelet -->
        <PreviewExamItem
          :title="$t('antiPlatelet')"
          :value="examinationInfo.antiplateletID"
          refKey="id"
          refValue="antiplatelet"
          :refArr="getAntiplateletList"
        >
          <template #extra>
            <span v-if="examinationInfo.antiplateletOther">
              其他 : {{ examinationInfo.antiplateletOther }}
            </span>
            <br v-if="examinationInfo.antiplateletOther" />
            <span>
              抗凝血藥物停藥天數
              {{ examinationInfo.antiplateletStopDays }} 天
            </span>
          </template>
        </PreviewExamItem>
        <!-- cleanseLevel(before) -->
        <PreviewExamItem
          :title="$t('cleanseLevel')"
          :value="[examinationInfo.cleanScale]"
          refKey="value"
          refValue="text"
          :refArr="getCleanScaleList"
        />
        <!-- cleanseLevelAfter -->
        <PreviewExamItem
          :title="$t('cleanseLevelAfter')"
          :value="[examinationInfo.cleanScaleAfter]"
          refKey="value"
          refValue="text"
          :refArr="getCleanScaleList"
        />
        <!-- incompleteStudy -->
        <PreviewExamItem
          :title="$t('incompleteStudy')"
          :value="[examinationInfo.incompleteDue]"
          refKey="value"
          refValue="text"
          :refArr="getIncompleteReasonList[2]"
        />
      </ul>
      <ul class="bowel-cleanse-forms__group bowel-cleanse-forms__group--second">
        <!-- Scoped Id -->
        <PreviewExamItem
          :title="$t('scopeId')"
          :value="[examinationInfo.inspection_instrumentID]"
          refKey="id"
          refValue="inspection_instrument"
          :refArr="getInspectionInstrumentList"
        />

        <!-- washingMachine -->
        <PreviewExamItem
          :title="$t('washingMachine')"
          :value="[examinationInfo.wash_instrumentID]"
          refKey="id"
          refValue="wash_instrument"
          :refArr="getWashInstrumentList"
        />

        <!-- premedication -->
        <PreviewExamItem
          :title="$t('premedication')"
          :value="examinationInfo.antispasmodicReason"
          refKey="value"
          refValue="text"
          prefix="原因: "
          :refArr="getNoUseAntipasmodicReasonList"
        >
          <template #extra-start>
            <div>
              {{
                Number(examinationInfo.antispasmodicHave) ? '有' : '無'
              }}使用止痙攣藥物
            </div>
          </template>
          <template #extra>
            <span v-if="examinationInfo.antispasmodicOther">
              其他 : {{ examinationInfo.antispasmodicOther }}
            </span>
          </template>
        </PreviewExamItem>

        <!-- sedatives -->
        <li>
          <h3>{{ $t('sedatives') }} :</h3>
          <div v-if="examination.sedatives.length" class="sedative-list">
            <div v-for="item in examination.sedatives">
              {{ getSedativeNameFromID(item.sedativeID) }} {{ item.value }}mg
            </div>
            <div v-if="examination.examination.sedativeOther">
              {{ $t('other') }} : {{ examination.examination.sedativeOther }}
            </div>
          </div>
          <p v-else>Nil</p>
        </li>
        <!-- insertionLevel -->
        <PreviewExamItem
          :title="$t('insertionLevel')"
          :value="[examinationInfo.insertionLevel]"
          refKey="value"
          refValue="text"
          :refArr="getInsertionLevelList"
        />
        <!-- insertionLength -->
        <PreviewExamItem
          :title="$t('insertionLength')"
          :value="[`${examinationInfo.insertionLength}cm`]"
          :needMapRef="false"
        />
        <!-- complication -->
        <PreviewExamItem
          :title="$t('complication')"
          :value="examinationInfo.complication"
          refKey="value"
          refValue="text"
          :refArr="getComplicationList"
        >
          <template #extra>
            <p v-if="examinationInfo.complicationOther">
              其他 :
              {{ examinationInfo.complicationOther }}
            </p>
          </template>
        </PreviewExamItem>
        <!-- addition remark -->
        <PreviewExamItem
          :title="$t('additionalRemarks')"
          :value="[examinationInfo.additionalRemarks]"
          :needMapRef="false"
        />
      </ul>
    </div>
    <div class="border-t">
      <time-info-group :data="examinationInfo" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import TimeInfoGroup from './TimeInfoGroup.vue'
import PreviewExamItem from './PreviewExamItem.vue'

export default {
  name: 'ColonPreviewBowelCleanseForms',

  components: {
    TimeInfoGroup,
    PreviewExamItem,
  },

  props: {
    examination: {
      type: Object,
      required: true,
    },

    sedatives: {
      type: Array,
    },
  },
  computed: {
    ...mapGetters('examinations', [
      'getIndicationList',
      'getComplicationList',
      'getCleanMedList',
      'getAntiplateletList',
      'getAntispasmodicList',
      'getNoUseAntipasmodicReasonList',
      'getInspectionInstrumentList',
      'getWashInstrumentList',
      'getCleanScaleList',
      'getInsertionLevelList',
      'getIncompleteReasonList',
    ]),
    examinationInfo() {
      return this.examination.examination
    },
  },
  methods: {
    getSedativeNameFromID(id) {
      return this.sedatives.filter((item) => item.id === id)[0].sedative
    },
    removeOthersFromList(list, val) {
      const othersValue = val
      return list.filter((item) => item !== othersValue)
    },
  },
}
</script>

<style lang="scss" scoped>
// @import '@/styles/components/bowelCleanseForms.scss';

.bowel-cleanse-forms.print {
  white-space: initial !important;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  ul,
  li {
    margin-left: 0;
    padding: 0;
  }
  li {
    list-style: none;
  }
}

.bowel-cleanse-forms__group {
  li {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    margin-bottom: 0.5rem;
  }
  p {
    align-self: center;
  }
  &--first li {
    grid-template-columns: 35% 65%;
  }
  &--second li {
    grid-template-columns: 30% 70%;
  }
}
@media (max-width: 600px) {
  .bowel-cleanse-forms.print {
    grid-template-columns: 1fr;
  }
  .bowel-cleanse-forms__group {
    li {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media print {
  .bowel-cleanse-forms.print {
    grid-template-columns: repeat(2, 1fr);
  }
  .bowel-cleanse-forms__group {
    &--first li {
      grid-template-columns: 53% 47%;
    }
    &--second li {
      grid-template-columns: 44% 56%;
    }
  }
}
</style>
